import React from 'react';

import { Skeleton, Stack } from '@mui/material';

interface SkeletonButtonProps {
  width?: number;
  height?: number;
  borderRadius?: string | number;
}

interface SkeletonButtonGroupProps {
  count: number;
  buttonProps?: Omit<SkeletonButtonProps, 'key'>;
  keys?: string[];
}

interface CalendarBodySkeletonProps {
  rowCount?: number;
}

const SkeletonButton: React.FC<SkeletonButtonProps> = ({ width = 50, height = 32, borderRadius = '4px' }) => (
  <Skeleton variant="rectangular" width={width} height={height} sx={{ borderRadius }} />
);

const SkeletonButtonGroup: React.FC<SkeletonButtonGroupProps> = ({ count, buttonProps, keys }) => (
  <Stack direction="row" spacing={0.5}>
    {Array.from({ length: count }).map((_, index) => (
      <SkeletonButton key={keys ? keys[index] : `btn-group-item-${count}-${index}`} {...buttonProps} />
    ))}
  </Stack>
);

const CalendarHeaderSkeleton = () => (
  <Stack
    direction="row"
    sx={{
      width: '100%',
      height: '40px',
      mb: 1,
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <SkeletonButtonGroup count={3} buttonProps={{ borderRadius: '15%' }} keys={['nav-prev', 'nav-today', 'nav-next']} />
    <Skeleton variant="text" sx={{ width: '10%', mx: 2, height: '80%' }} />
    <SkeletonButtonGroup
      count={4}
      buttonProps={{ borderRadius: '4px' }}
      keys={['view-day', 'view-week', 'view-month', 'view-year']}
    />
  </Stack>
);

const DAY_LABELS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const DayLabelsSkeleton = () => (
  <Stack direction="row" spacing={0.25} sx={{ width: '100%', height: '30px' }}>
    {DAY_LABELS.map((day) => (
      <Skeleton key={`day-label-${day}`} variant="text" sx={{ flexGrow: 1, height: '100%' }} />
    ))}
  </Stack>
);

const WEEK_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const WeekRowSkeleton: React.FC = () => (
  <Stack direction="row" spacing={0.25} sx={{ flexGrow: 1, width: '100%' }}>
    {WEEK_DAYS.map((day) => (
      <Skeleton key={`cell-${day}`} variant="rectangular" sx={{ flexGrow: 1, height: '100%' }} />
    ))}
  </Stack>
);

const CalendarBodySkeleton: React.FC<CalendarBodySkeletonProps> = ({ rowCount = 5 }) => (
  <Stack spacing={0.25} sx={{ width: '100%', flexGrow: 1 }}>
    <DayLabelsSkeleton />
    <Stack spacing={0.25} sx={{ width: '100%', flexGrow: 1 }}>
      {Array.from({ length: rowCount }, (_, rowIndex) => rowIndex + 1).map((rowId) => (
        <WeekRowSkeleton key={`week-row-${rowId}`} />
      ))}
    </Stack>
  </Stack>
);

const CalendarSkeleton = () => {
  return (
    <Stack spacing={1} sx={{ width: '100%', height: '100%', p: 0.5 }}>
      <CalendarHeaderSkeleton />
      <CalendarBodySkeleton rowCount={5} />
    </Stack>
  );
};

export default CalendarSkeleton;
